import applyCaseMiddleware from "axios-case-converter"; // TODO: かなりニッチなライブラリのため、自前で実装するか他のライブラリを探す
import axios, {
  type AxiosInstance,
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import { useCookies, Cookies } from "react-cookie";
import { type IJwtResponse } from "../types/jwt";
import { type ICookiesType } from "../types/cookies";
import { baseURL } from "../configs";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import type { User } from "@/types/localstorage";
import { clearAuthData, saveIpNotAllowedReturnPath } from "@/utils/auth";
import {
  clearIpNotAllowed,
  IP_NOT_ALLOWED_PATH,
  isIpNotAllowedError,
  isIpNotAllowedState,
  markIpNotAllowed,
  restoreBinaryErrorResponse,
} from "@/utils/ipRestriction";

// リトライ済みフラグを持つ拡張型
interface AxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface IUseApiClientReturnType {
  apiClient: AxiosInstance;
}

// JWTリフレッシュAPIのパス
const REFRESH_TOKEN_ENDPOINT = "auth/jwt/refresh";

// ログインAPIのパス。認証前のエンドポイントのため401でのトークン更新対象から除外する
const LOGIN_ENDPOINT = "auth/jwt/create";

const useApiClient = (): IUseApiClientReturnType => {
  const [cookies, setCookie, removeCookie] = useCookies();
  const navigate = useNavigate();
  const [, , removeValue] = useLocalStorage<User | undefined>(
    "user",
    undefined
  );
  const { mutate } = useSWRConfig();

  const apiClient: AxiosInstance = applyCaseMiddleware(
    axios.create({
      baseURL,
      timeout: 120_000, // ms (BEのタイムアウト設定に合わせる)
    })
  );

  apiClient.interceptors.request.use((config) => {
    const c = new Cookies();
    const accesstoken = c.get<string>("accesstoken");
    config.headers.Authorization = `JWT ${accesstoken}`;
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => {
      // 通信が成立した＝許可されたネットワークに戻っている
      if (isIpNotAllowedState()) {
        clearIpNotAllowed();
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | AxiosRequestConfigWithRetry
        | undefined;

      // ファイルダウンロードや地図タイルはバイナリで受け取るため、そのままでは
      // エラーボディのcodeを読めない。判定の前にJSONへ復元する
      await restoreBinaryErrorResponse(error);

      // 許可IP外は認証情報の問題ではないため、ログイン状態は保ったまま専用画面へ誘導する
      // 許可ネットワークへ戻れば再ログイン無しで再開できる
      // トークン更新・検証の403もここで受けるため、リフレッシュの分岐より先に判定する
      if (isIpNotAllowedError(error)) {
        // 最初に拒否された画面を覚えておき、再開時にそこへ戻す。
        // 並行リクエストや専用画面からの再確認で上書きしないよう、初回のみ記録する
        if (!isIpNotAllowedState()) {
          saveIpNotAllowedReturnPath(
            window.location.pathname + window.location.search
          );
        }
        markIpNotAllowed();
        // 取得済みデータがSWRのキャッシュに残っていると、アプリ画面へ戻った際に
        // 許可外ネットワークで業務データが表示されてしまうため破棄する
        void mutate(() => true, undefined, { revalidate: false });
        // 並行リクエストが同じ遷移を繰り返しても履歴を積まないようreplaceで遷移する
        navigate(IP_NOT_ALLOWED_PATH, { replace: true });
        return await Promise.reject(error);
      }

      // 無限ループ防止：リフレッシュリクエストでのエラーは除外
      if (error.config?.url === REFRESH_TOKEN_ENDPOINT) {
        // リフレッシュトークンが期限切れの場合はログイン画面に遷移
        // useLogin.tsのlogout()と同等の処理（共通化したclearAuthData関数を使用）
        clearAuthData(removeCookie, setCookie, removeValue);
        // 別ユーザーで入り直した際に前ユーザーの情報（現在ユーザー・会員判定含む）が
        // 残らないよう、SWRキャッシュを破棄する（logout()と揃える）
        void mutate(() => true, undefined, { revalidate: false });
        navigate("/login");
        return await Promise.reject(error);
      }

      // API通信時にトークン切れになった場合、トークンを更新する
      // ログインAPIは認証前のエンドポイントなので除外する。含めると、401（認証失敗）で
      // トークン更新が走り、成功した場合に誤った認証情報のままログインAPIを再送してしまう
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        error.config?.url !== LOGIN_ENDPOINT
      ) {
        // リトライフラグを立てて無限ループを防止
        originalRequest._retry = true;

        const { refreshtoken } = cookies as ICookiesType;

        // refreshtokenがundefinedの場合は、どうやってもアクセストークンは得られないので"auth/jwt/refresh"の呼び出しは諦める
        if (refreshtoken === undefined) {
          return await Promise.reject(error);
        }

        try {
          const res: AxiosResponse<IJwtResponse> = await apiClient.post(
            REFRESH_TOKEN_ENDPOINT,
            {
              refresh: refreshtoken,
            }
          );

          setCookie("accesstoken", res.data.access, { path: "/" });
          setCookie("refreshtoken", res.data.refresh, { path: "/" });

          // 元のリクエストを新しいトークンで再実行
          originalRequest.headers.Authorization = `JWT ${res.data.access}`;
          return await apiClient(originalRequest);
        } catch (refreshError) {
          // リフレッシュ失敗時はエラーをそのまま返す（ログイン画面への遷移はリフレッシュエンドポイントのエラーハンドリングで行われる）
          return await Promise.reject(refreshError);
        }
      }
      return await Promise.reject(error);
    }
  );

  return { apiClient };
};

export { useApiClient };
